/**
 * 权限溯源页面
 * 提供用户权限溯源可视化（三层决策流程图）和批量溯源功能
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Input, Button, Select, Tag, Table, Divider,
  Space, Spin, Alert, message, Typography, Tabs, Statistic, Progress, Tooltip,
  Row, Col, Empty,
} from 'antd';
import {
  SearchOutlined, CheckCircleOutlined, CloseCircleOutlined,
  MinusCircleOutlined, ThunderboltOutlined, RightOutlined,
  UserOutlined, SafetyOutlined, KeyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { tracePermission, batchCheckPermissions } from '../../api/permissionEngine';
import { getUserList } from '../../api/user';
import { getRolePermissions } from '../../api/role';
import type {
  TracePermissionResult,
  TraceLayerResult,
  PermissionCheckResult,
  User,
} from '../../api/types';

const { TextArea } = Input;
const { Text, Title } = Typography;

/** Layer display config */
const layerConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  super_admin: { label: 'SuperAdmin', icon: <SafetyOutlined />, color: '#722ed1' },
  role:        { label: 'Role 角色', icon: <UserOutlined />, color: '#1677ff' },
  override:    { label: 'Override 覆盖', icon: <KeyOutlined />, color: '#fa8c16' },
  data_scope:  { label: 'DataScope', icon: <SafetyOutlined />, color: '#13c2c2' },
  field:       { label: 'Field 字段', icon: <KeyOutlined />, color: '#52c41a' },
  base:        { label: 'Base 基础', icon: <SafetyOutlined />, color: '#597ef7' },
  template:    { label: 'Template 模板', icon: <KeyOutlined />, color: '#eb2f96' },
};

/** Get result color */
const getResultColor = (result: string) => {
  if (result === 'allow' || result === 'grant') return '#52c41a';
  if (result === 'deny') return '#ff4d4f';
  return '#faad14';
};

/** Batch trace result row */
interface BatchTraceRow {
  key: string;
  permissionCode: string;
  allowed: boolean;
  source: string;
  reason?: string;
  layers?: TraceLayerResult[];
}

/* ===== Flowchart Component ===== */
const FlowchartTrace: React.FC<{ layers: TraceLayerResult[]; finalAllowed: boolean }> = ({
  layers,
  finalAllowed,
}) => {
  return (
    <div style={{ overflowX: 'auto', padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content', gap: 0 }}>
        {/* Start node */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          minWidth: 60,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: '#f0f5ff', border: '2px solid #1677ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, color: '#1677ff',
          }}>
            <UserOutlined />
          </div>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>请求</Text>
        </div>

        {layers.map((layer, idx) => {
          const config = layerConfig[layer.layer] || {
            label: layer.layer, icon: <SafetyOutlined />, color: '#999',
          };
          const isChecked = layer.checked;
          const isAllow = layer.result === 'allow' || layer.result === 'grant';
          const isDeny = layer.result === 'deny';
          const resultColor = !isChecked ? '#d9d9d9' : getResultColor(layer.result);
          const bgColor = !isChecked ? '#fafafa' : isAllow ? '#f6ffed' : isDeny ? '#fff2f0' : '#fffbe6';
          const borderColor = !isChecked ? '#d9d9d9' : resultColor;

          return (
            <React.Fragment key={layer.layer}>
              {/* Arrow */}
              <div style={{
                display: 'flex', alignItems: 'center',
                color: isChecked ? resultColor : '#d9d9d9',
                padding: '0 4px',
              }}>
                <div style={{
                  width: 32, height: 2,
                  background: isChecked ? resultColor : '#d9d9d9',
                }} />
                <RightOutlined style={{ fontSize: 10 }} />
              </div>

              {/* Layer node */}
              <Tooltip
                title={layer.detail || (isChecked ? `结果: ${layer.result}` : '未检查')}
              >
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  minWidth: 90, cursor: 'default',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 8,
                    background: bgColor,
                    border: `2px solid ${borderColor}`,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    position: 'relative', transition: 'all 0.3s',
                  }}>
                    <span style={{ fontSize: 18, color: resultColor }}>
                      {isChecked ? (
                        isAllow ? <CheckCircleOutlined /> :
                        isDeny ? <CloseCircleOutlined /> :
                        <MinusCircleOutlined />
                      ) : (
                        <MinusCircleOutlined />
                      )}
                    </span>
                    {isChecked && (
                      <Tag
                        color={isAllow ? 'success' : isDeny ? 'error' : 'warning'}
                        style={{
                          position: 'absolute', bottom: -10, fontSize: 10,
                          lineHeight: '16px', padding: '0 4px', margin: 0,
                        }}
                      >
                        {layer.result}
                      </Tag>
                    )}
                  </div>
                  <Text style={{
                    fontSize: 11, marginTop: 12, color: config.color,
                    fontWeight: 500, textAlign: 'center', maxWidth: 90,
                  }}>
                    {config.label}
                  </Text>
                  {layer.detail && (
                    <Text type="secondary" style={{
                      fontSize: 10, maxWidth: 100, textAlign: 'center',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {layer.detail}
                    </Text>
                  )}
                </div>
              </Tooltip>
            </React.Fragment>
          );
        })}

        {/* Final result arrow */}
        <div style={{
          display: 'flex', alignItems: 'center',
          color: finalAllowed ? '#52c41a' : '#ff4d4f',
          padding: '0 4px',
        }}>
          <div style={{
            width: 32, height: 2,
            background: finalAllowed ? '#52c41a' : '#ff4d4f',
          }} />
          <RightOutlined style={{ fontSize: 10 }} />
        </div>

        {/* Final result node */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          minWidth: 70,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: finalAllowed ? '#f6ffed' : '#fff2f0',
            border: `3px solid ${finalAllowed ? '#52c41a' : '#ff4d4f'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: finalAllowed ? '#52c41a' : '#ff4d4f',
          }}>
            {finalAllowed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          </div>
          <Text strong style={{
            fontSize: 12, marginTop: 4,
            color: finalAllowed ? '#52c41a' : '#ff4d4f',
          }}>
            {finalAllowed ? '允许' : '拒绝'}
          </Text>
        </div>
      </div>
    </div>
  );
};

/* ===== Mini Flowchart for batch expandable rows ===== */
const MiniFlowchart: React.FC<{ layers: TraceLayerResult[] }> = ({ layers }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', padding: '8px 0' }}>
    {layers.map((layer, idx) => {
      const config = layerConfig[layer.layer] || { label: layer.layer, color: '#999' };
      const isChecked = layer.checked;
      const isAllow = layer.result === 'allow' || layer.result === 'grant';
      const isDeny = layer.result === 'deny';
      return (
        <React.Fragment key={layer.layer}>
          {idx > 0 && (
            <RightOutlined style={{ fontSize: 8, color: '#d9d9d9' }} />
          )}
          <Tooltip title={`${config.label}: ${isChecked ? layer.result : '未检查'}${layer.detail ? ` - ${layer.detail}` : ''}`}>
            <Tag
              color={!isChecked ? 'default' : isAllow ? 'success' : isDeny ? 'error' : 'warning'}
              style={{ margin: 0, cursor: 'default' }}
            >
              {config.label}
            </Tag>
          </Tooltip>
        </React.Fragment>
      );
    })}
  </div>
);

const PermissionTracingPage: React.FC = () => {
  // --- User selector state ---
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // --- Trace state ---
  const [permissionCode, setPermissionCode] = useState('');
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceResult, setTraceResult] = useState<TracePermissionResult | null>(null);
  const [traceError, setTraceError] = useState<string | null>(null);

  // --- Batch state ---
  const [batchCodes, setBatchCodes] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchTraceRow[]>([]);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchTraceMode, setBatchTraceMode] = useState(false);

  // --- User all permissions state ---
  const [userPermsLoading, setUserPermsLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState<any[]>([]);
  const [userPermsUserId, setUserPermsUserId] = useState<number | null>(null);

  // Debounce timer ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Load initial users ---
  useEffect(() => {
    fetchUsers('');
  }, []);

  /** Fetch users with optional search keyword */
  const fetchUsers = async (keyword: string) => {
    setUserSearchLoading(true);
    try {
      const params: { current?: number; pageSize?: number; username?: string; realName?: string } = {
        current: 1,
        pageSize: 20,
      };
      if (keyword) {
        params.username = keyword;
      }
      const res = await getUserList(params);
      if (res?.data) {
        const users = Array.isArray(res.data) ? res.data : [];
        setUserOptions(users);
      }
    } catch (err) {
      console.error('获取用户列表失败', err);
    } finally {
      setUserSearchLoading(false);
    }
  };

  /** Debounced user search handler */
  const handleUserSearch = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchUsers(value);
    }, 300);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /** Handle trace permission */
  const handleTrace = async () => {
    if (!selectedUserId) {
      message.warning('请选择用户');
      return;
    }
    if (!permissionCode.trim()) {
      message.warning('请输入权限码');
      return;
    }

    setTraceLoading(true);
    setTraceError(null);
    setTraceResult(null);

    try {
      const res = await tracePermission({
        userId: selectedUserId,
        permissionCode: permissionCode.trim(),
      });

      if (res?.data) {
        setTraceResult(res.data as any);
      }
    } catch (err: any) {
      const msg = err?.message || '溯源查询失败';
      setTraceError(msg);
      console.error('权限溯源失败', err);
    } finally {
      setTraceLoading(false);
    }
  };

  /** Handle batch check (simple mode) */
  const handleBatchCheck = async () => {
    if (!selectedUserId) {
      message.warning('请选择用户');
      return;
    }
    const codes = batchCodes
      .split(/[\n,;]/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (codes.length === 0) {
      message.warning('请输入至少一个权限码');
      return;
    }

    setBatchLoading(true);
    setBatchError(null);
    setBatchResults([]);
    setBatchTraceMode(false);

    try {
      const res = await batchCheckPermissions({
        userId: selectedUserId,
        permissionCodes: codes,
      });

      if (res?.data) {
        const data = res.data as any;
        const results: Record<string, PermissionCheckResult> = data.results || {};
        const rows: BatchTraceRow[] = Object.entries(results).map(
          ([code, checkResult]) => ({
            key: code,
            permissionCode: code,
            allowed: checkResult.allowed,
            source: checkResult.source,
            reason: checkResult.reason,
          })
        );
        setBatchResults(rows);
      }
    } catch (err: any) {
      const msg = err?.message || '批量检查失败';
      setBatchError(msg);
      console.error('批量权限检查失败', err);
    } finally {
      setBatchLoading(false);
    }
  };

  /** Handle batch trace (full tracing for each permission) */
  const handleBatchTrace = async () => {
    if (!selectedUserId) {
      message.warning('请选择用户');
      return;
    }
    const codes = batchCodes
      .split(/[\n,;]/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (codes.length === 0) {
      message.warning('请输入至少一个权限码');
      return;
    }
    if (codes.length > 20) {
      message.warning('批量溯源最多支持 20 个权限码');
      return;
    }

    setBatchLoading(true);
    setBatchError(null);
    setBatchResults([]);
    setBatchTraceMode(true);

    try {
      // Trace each permission in parallel
      const results = await Promise.allSettled(
        codes.map((code) =>
          tracePermission({ userId: selectedUserId!, permissionCode: code })
        )
      );

      const rows: BatchTraceRow[] = results.map((result, i) => {
        if (result.status === 'fulfilled' && result.value?.data) {
          const data = result.value.data as any;
          return {
            key: codes[i],
            permissionCode: codes[i],
            allowed: data.finalResult?.allowed ?? false,
            source: data.finalResult?.source ?? '',
            reason: data.finalResult?.reason,
            layers: data.layers,
          };
        }
        return {
          key: codes[i],
          permissionCode: codes[i],
          allowed: false,
          source: 'error',
          reason: result.status === 'rejected' ? '溯源失败' : '无数据',
        };
      });

      setBatchResults(rows);
    } catch (err: any) {
      setBatchError(err?.message || '批量溯源失败');
    } finally {
      setBatchLoading(false);
    }
  };

  /** Fetch all permissions for a user (via roles) */
  const fetchUserPermissions = useCallback(async (userId: number) => {
    if (userPermsUserId === userId && userPermissions.length > 0) return;
    setUserPermsLoading(true);
    setUserPermsUserId(userId);
    try {
      // Use batch check as a fallback: the API returns user's effective permissions
      // For now, we show the trace result as the user's permissions context
      const res: any = await batchCheckPermissions({
        userId,
        permissionCodes: ['*'], // wildcard to get all
      });
      const data = res?.data?.data || res?.data;
      if (data?.permissions && Array.isArray(data.permissions)) {
        setUserPermissions(data.permissions);
      } else if (data?.results) {
        // Convert results map to array
        const perms = Object.entries(data.results).map(([code, result]: [string, any]) => ({
          code,
          name: code,
          allowed: result.allowed,
          source: result.source,
          reason: result.reason,
        }));
        setUserPermissions(perms);
      } else {
        setUserPermissions([]);
      }
    } catch {
      setUserPermissions([]);
    } finally {
      setUserPermsLoading(false);
    }
  }, [userPermsUserId, userPermissions.length]);

  /** Batch statistics */
  const batchStats = {
    total: batchResults.length,
    allowed: batchResults.filter((r) => r.allowed).length,
    denied: batchResults.filter((r) => !r.allowed).length,
  };

  /** Batch result table columns */
  const batchColumns: ColumnsType<BatchTraceRow> = [
    {
      title: '权限码',
      dataIndex: 'permissionCode',
      key: 'permissionCode',
      ellipsis: true,
      render: (code: string) => <code style={{ fontSize: 12 }}>{code}</code>,
    },
    {
      title: '结果',
      dataIndex: 'allowed',
      key: 'allowed',
      width: 90,
      render: (allowed: boolean) => (
        <Tag color={allowed ? 'success' : 'error'} icon={allowed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {allowed ? '允许' : '拒绝'}
        </Tag>
      ),
      filters: [
        { text: '允许', value: true },
        { text: '拒绝', value: false },
      ],
      onFilter: (value, record) => record.allowed === value,
    },
    {
      title: '决策来源',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (source: string) => {
        const config = layerConfig[source];
        return config ? (
          <Tag color={config.color} style={{ margin: 0 }}>{config.label}</Tag>
        ) : (
          <span>{source || '-'}</span>
        );
      },
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason?: string) => reason || '-',
    },
  ];

  /** User selector shared component */
  const UserSelector = (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
        选择用户
      </Text>
      <Select
        showSearch
        placeholder="搜索并选择用户"
        style={{ width: 260 }}
        loading={userSearchLoading}
        filterOption={false}
        onSearch={handleUserSearch}
        onChange={(value: number) => setSelectedUserId(value)}
        value={selectedUserId}
        allowClear
        onClear={() => setSelectedUserId(null)}
        notFoundContent={userSearchLoading ? <Spin size="small" /> : '无匹配用户'}
      >
        {userOptions.map((user) => (
          <Select.Option key={user.id} value={Number(user.id)}>
            {user.realName ? `${user.realName} (${user.username})` : user.username}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  return (
    <Card title="权限溯源" bordered={false}>
      <Tabs
        defaultActiveKey="single"
        onChange={(key) => {
          if (key === 'userPerms' && selectedUserId && userPermsUserId !== selectedUserId) {
            fetchUserPermissions(selectedUserId);
          }
        }}
        items={[
          {
            key: 'single',
            label: '单权限溯源',
            children: (
              <>
                {/* Query form */}
                <div style={{ marginBottom: 24 }}>
                  <Space wrap size="middle" align="start">
                    {UserSelector}
                    <div>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        权限码
                      </Text>
                      <Input
                        placeholder="如: user:create, order:delete"
                        style={{ width: 280 }}
                        value={permissionCode}
                        onChange={(e) => setPermissionCode(e.target.value)}
                        onPressEnter={handleTrace}
                      />
                    </div>
                    <div style={{ paddingTop: 22 }}>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={traceLoading}
                        onClick={handleTrace}
                      >
                        溯源
                      </Button>
                    </div>
                  </Space>
                </div>

                {/* Trace loading */}
                {traceLoading && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" tip="正在溯源权限评估路径..."><div style={{ minHeight: 100 }} /></Spin>
                  </div>
                )}

                {/* Trace error */}
                {traceError && (
                  <Alert
                    type="error"
                    message="溯源失败"
                    description={traceError}
                    showIcon
                    closable
                    onClose={() => setTraceError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Trace result */}
                {traceResult && !traceLoading && (
                  <>
                    {/* Final result banner */}
                    <Card
                      style={{
                        marginBottom: 24,
                        borderColor: traceResult.finalResult.allowed ? '#52c41a' : '#ff4d4f',
                        borderWidth: 2,
                      }}
                      styles={{ body: { padding: 20 } }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {traceResult.finalResult.allowed ? (
                          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                        ) : (
                          <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                        )}
                        <div>
                          <Title
                            level={3}
                            style={{
                              margin: 0,
                              color: traceResult.finalResult.allowed ? '#52c41a' : '#ff4d4f',
                            }}
                          >
                            {traceResult.finalResult.allowed ? '权限允许' : '权限拒绝'}
                          </Title>
                          <div style={{ marginTop: 4, color: '#666' }}>
                            <Space split={<Divider type="vertical" />}>
                              <span>权限码: <code>{traceResult.permissionCode}</code></span>
                              <span>来源: {traceResult.finalResult.source}</span>
                              <span>用户ID: {traceResult.userId}</span>
                              <span>租户ID: {traceResult.tenantId}</span>
                            </Space>
                          </div>
                          {traceResult.finalResult.reason && (
                            <div style={{ marginTop: 4, color: '#999' }}>
                              原因: {traceResult.finalResult.reason}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>

                    {/* Flowchart visualization */}
                    <Card title="决策流程图" size="small" style={{ marginBottom: 24 }}>
                      <FlowchartTrace
                        layers={traceResult.layers}
                        finalAllowed={traceResult.finalResult.allowed}
                      />
                    </Card>

                    {/* Layer details table */}
                    <Card title="各层评估详情" size="small">
                      <Table
                        rowKey="layer"
                        dataSource={traceResult.layers}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: '评估层',
                            dataIndex: 'layer',
                            width: 140,
                            render: (layer: string) => {
                              const config = layerConfig[layer] || { label: layer, color: '#999' };
                              return <Tag color={config.color}>{config.label}</Tag>;
                            },
                          },
                          {
                            title: '是否检查',
                            dataIndex: 'checked',
                            width: 100,
                            render: (checked: boolean) => (
                              <Tag color={checked ? 'processing' : 'default'}>
                                {checked ? '已检查' : '跳过'}
                              </Tag>
                            ),
                          },
                          {
                            title: '结果',
                            dataIndex: 'result',
                            width: 100,
                            render: (result: string, record: TraceLayerResult) => {
                              if (!record.checked) return <span style={{ color: '#999' }}>-</span>;
                              return (
                                <Tag color={
                                  result === 'allow' || result === 'grant' ? 'success' :
                                  result === 'deny' ? 'error' : 'warning'
                                }>
                                  {result}
                                </Tag>
                              );
                            },
                          },
                          {
                            title: '详情',
                            dataIndex: 'detail',
                            render: (detail?: string) => detail || '-',
                          },
                        ]}
                      />
                    </Card>
                  </>
                )}

                {/* Empty state */}
                {!traceResult && !traceLoading && !traceError && (
                  <Empty
                    description="选择用户并输入权限码开始溯源"
                    style={{ padding: '60px 0' }}
                  />
                )}
              </>
            ),
          },
          {
            key: 'batch',
            label: '批量溯源',
            children: (
              <>
                {/* Batch input form */}
                <div style={{ marginBottom: 24 }}>
                  <Row gutter={24}>
                    <Col span={16}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                          {UserSelector}
                        </div>
                        <div>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                            权限码列表（每行一个，或用逗号/分号分隔，溯源模式最多20个）
                          </Text>
                          <TextArea
                            placeholder={'user:create\nuser:delete\norder:view\nproduct:edit'}
                            style={{ width: '100%' }}
                            rows={5}
                            value={batchCodes}
                            onChange={(e) => setBatchCodes(e.target.value)}
                          />
                        </div>
                        <Space>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={batchLoading}
                            onClick={handleBatchTrace}
                          >
                            批量溯源（含流程图）
                          </Button>
                          <Button
                            icon={<SearchOutlined />}
                            loading={batchLoading}
                            onClick={handleBatchCheck}
                          >
                            快速检查
                          </Button>
                        </Space>
                      </Space>
                    </Col>
                    <Col span={8}>
                      {batchResults.length > 0 && (
                        <Card size="small" style={{ height: '100%' }}>
                          <Row gutter={[16, 16]}>
                            <Col span={8}>
                              <Statistic title="总计" value={batchStats.total} />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="允许"
                                value={batchStats.allowed}
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="拒绝"
                                value={batchStats.denied}
                                valueStyle={{ color: '#ff4d4f' }}
                              />
                            </Col>
                            <Col span={24}>
                              <Text type="secondary" style={{ fontSize: 12 }}>通过率</Text>
                              <Progress
                                percent={batchStats.total > 0
                                  ? Math.round((batchStats.allowed / batchStats.total) * 100)
                                  : 0}
                                strokeColor="#52c41a"
                                size="small"
                              />
                            </Col>
                          </Row>
                        </Card>
                      )}
                    </Col>
                  </Row>
                </div>

                <Divider />

                {/* Batch error */}
                {batchError && (
                  <Alert
                    type="error"
                    message="批量操作失败"
                    description={batchError}
                    showIcon
                    closable
                    onClose={() => setBatchError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Batch loading */}
                {batchLoading && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" tip="正在批量溯源..."><div style={{ minHeight: 100 }} /></Spin>
                  </div>
                )}

                {/* Batch results */}
                {batchResults.length > 0 && !batchLoading && (
                  <Table
                    rowKey="key"
                    columns={batchColumns}
                    dataSource={batchResults}
                    pagination={false}
                    size="middle"
                    expandable={batchTraceMode ? {
                      expandedRowRender: (record) => (
                        record.layers ? (
                          <div style={{ padding: '8px 0' }}>
                            <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                              评估路径：
                            </Text>
                            <MiniFlowchart layers={record.layers} />
                            <Table
                              rowKey="layer"
                              dataSource={record.layers}
                              pagination={false}
                              size="small"
                              style={{ marginTop: 8 }}
                              columns={[
                                {
                                  title: '评估层',
                                  dataIndex: 'layer',
                                  width: 120,
                                  render: (layer: string) => {
                                    const cfg = layerConfig[layer] || { label: layer, color: '#999' };
                                    return <Tag color={cfg.color}>{cfg.label}</Tag>;
                                  },
                                },
                                {
                                  title: '状态',
                                  dataIndex: 'checked',
                                  width: 80,
                                  render: (checked: boolean) => (
                                    <Tag color={checked ? 'processing' : 'default'}>
                                      {checked ? '已检查' : '跳过'}
                                    </Tag>
                                  ),
                                },
                                {
                                  title: '结果',
                                  dataIndex: 'result',
                                  width: 80,
                                  render: (result: string, r: TraceLayerResult) => {
                                    if (!r.checked) return '-';
                                    return (
                                      <Tag color={
                                        result === 'allow' || result === 'grant' ? 'success' :
                                        result === 'deny' ? 'error' : 'warning'
                                      }>
                                        {result}
                                      </Tag>
                                    );
                                  },
                                },
                                {
                                  title: '详情',
                                  dataIndex: 'detail',
                                  render: (d?: string) => d || '-',
                                },
                              ]}
                            />
                          </div>
                        ) : (
                          <Text type="secondary">无溯源数据</Text>
                        )
                      ),
                      rowExpandable: (record) => !!record.layers?.length,
                    } : undefined}
                  />
                )}

                {/* Batch empty state */}
                {batchResults.length === 0 && !batchLoading && !batchError && (
                  <Empty
                    description="输入权限码列表，选择批量溯源或快速检查"
                    style={{ padding: '60px 0' }}
                  />
                )}
              </>
            ),
          },
          {
            key: 'userPerms',
            label: '用户全部权限',
            children: (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Space wrap size="middle" align="start">
                    {UserSelector}
                    <div style={{ paddingTop: 22 }}>
                      <Button
                        type="primary"
                        icon={<SafetyOutlined />}
                        loading={userPermsLoading}
                        onClick={() => {
                          if (!selectedUserId) {
                            message.warning('请选择用户');
                            return;
                          }
                          setUserPermissions([]);
                          setUserPermsUserId(null);
                          fetchUserPermissions(selectedUserId);
                        }}
                      >
                        查询权限
                      </Button>
                    </div>
                  </Space>
                </div>

                {userPermsLoading && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" tip="正在查询用户权限..."><div style={{ minHeight: 100 }} /></Spin>
                  </div>
                )}

                {userPermissions.length > 0 && !userPermsLoading && (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <Space size="large">
                        <Statistic title="总权限数" value={userPermissions.length} />
                        <Statistic
                          title="允许"
                          value={userPermissions.filter((p) => p.allowed).length}
                          valueStyle={{ color: '#52c41a' }}
                        />
                        <Statistic
                          title="拒绝"
                          value={userPermissions.filter((p) => !p.allowed).length}
                          valueStyle={{ color: '#ff4d4f' }}
                        />
                      </Space>
                    </div>
                    <Table
                      rowKey="code"
                      dataSource={userPermissions}
                      pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
                      size="small"
                      columns={[
                        {
                          title: '权限编码',
                          dataIndex: 'code',
                          ellipsis: true,
                          render: (code: string) => <code style={{ fontSize: 12 }}>{code}</code>,
                        },
                        {
                          title: '权限名称',
                          dataIndex: 'name',
                          ellipsis: true,
                          render: (name: string, record: any) => name || record.title || record.code,
                        },
                        {
                          title: '状态',
                          dataIndex: 'allowed',
                          width: 90,
                          render: (allowed: boolean) => (
                            <Tag
                              color={allowed ? 'success' : 'error'}
                              icon={allowed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            >
                              {allowed ? '允许' : '拒绝'}
                            </Tag>
                          ),
                          filters: [
                            { text: '允许', value: true },
                            { text: '拒绝', value: false },
                          ],
                          onFilter: (value: any, record: any) => record.allowed === value,
                        },
                        {
                          title: '来源',
                          dataIndex: 'source',
                          width: 120,
                          render: (source: string) => {
                            const config = layerConfig[source];
                            return config ? (
                              <Tag color={config.color}>{config.label}</Tag>
                            ) : (
                              <span>{source || '-'}</span>
                            );
                          },
                        },
                      ]}
                    />
                  </>
                )}

                {userPermissions.length === 0 && !userPermsLoading && (
                  <Empty
                    description={userPermsUserId ? '未查询到权限数据' : '选择用户后查询其全部权限'}
                    style={{ padding: '60px 0' }}
                  />
                )}
              </>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default PermissionTracingPage;
